import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { C, S } from '../../../utils/styles';
import Icon from '../../Icon';
import { useToast } from '../../Toast';
import {
  AUDIENCE_OPTIONS,
  audienceLabel,
  certificateVerifyUrl,
  listAllCertificates,
  listCourses,
  reinstateCertificate,
  revokeCertificate,
} from './api';

// ----------------------------------------------------------------------------
// Phase 18.3 — Admin Certifications panel
// ----------------------------------------------------------------------------
//   * Lists all issued certificates (super user sees everything; AMs fall back
//     to their own personal certs via the backend).
//   * Filters: audience, course, status, free-text search, date range.
//   * Actions: open verification page (new tab), copy verify link, revoke /
//     reinstate (super only).
// ----------------------------------------------------------------------------

const STATUS_OPTIONS = [
  { value: '', label: 'All certificates' },
  { value: 'valid', label: 'Valid only' },
  { value: 'revoked', label: 'Revoked only' },
];

function formatDate(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (_) {
    return '—';
  }
}

function statusBadge(cert) {
  if (cert.revokedAt) {
    return { bg: C.redLight, fg: C.red, border: `1px solid ${C.red}33`, label: 'Revoked' };
  }
  return { bg: C.greenLight, fg: C.green, border: `1px solid ${C.greenBorder}`, label: 'Valid' };
}

function audienceBadge(value) {
  if (value === 'account_manager') return { bg: C.accentBg, fg: C.accent };
  if (value === 'employer') return { bg: C.purpleLight, fg: C.purple };
  if (value === 'employee') return { bg: C.orangeLight, fg: C.orange };
  return { bg: C.bg, fg: C.textMid };
}

export default function CertificationsAdminPanel() {
  const showToast = useToast();
  const [certs, setCerts] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [audienceFilter, setAudienceFilter] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listAllCertificates({
        audience: audienceFilter || undefined,
        courseId: courseFilter || undefined,
        status: statusFilter || undefined,
        q: q || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        limit: 500,
      });
      setCerts(Array.isArray(data) ? data : []);
    } catch (err) {
      showToast(err.message || 'Failed to load certificates', 'error');
    } finally {
      setLoading(false);
    }
  }, [audienceFilter, courseFilter, statusFilter, q, dateFrom, dateTo, showToast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Load courses once for the course filter dropdown.
    listCourses().then((data) => {
      setCourses(Array.isArray(data) ? data : []);
    }).catch(() => { /* silent */ });
  }, []);

  const clearFilters = () => {
    setAudienceFilter('');
    setCourseFilter('');
    setStatusFilter('');
    setQ('');
    setDateFrom('');
    setDateTo('');
  };

  const filteredCount = certs.length;
  const validCount = useMemo(() => certs.filter((c) => !c.revokedAt).length, [certs]);
  const revokedCount = filteredCount - validCount;

  const handleCopyLink = (cert) => {
    const url = certificateVerifyUrl(cert.id);
    try {
      navigator.clipboard.writeText(url);
      showToast('Verification link copied to clipboard', 'success');
    } catch (_) {
      // Fallback
      window.prompt('Copy verification URL:', url);
    }
  };

  const handleOpenVerify = (cert) => {
    window.open(certificateVerifyUrl(cert.id), '_blank', 'noopener,noreferrer');
  };

  const handleRevoke = async (cert) => {
    if (!window.confirm(`Revoke certificate ${cert.id} for ${cert.userName || cert.userEmail}?\n\nThe public verification page will report it as revoked. You can reinstate it later.`)) {
      return;
    }
    try {
      await revokeCertificate(cert.id);
      showToast('Certificate revoked', 'success');
      load();
    } catch (err) {
      showToast(err.message || 'Failed to revoke certificate', 'error');
    }
  };

  const handleReinstate = async (cert) => {
    try {
      await reinstateCertificate(cert.id);
      showToast('Certificate reinstated', 'success');
      load();
    } catch (err) {
      showToast(err.message || 'Failed to reinstate certificate', 'error');
    }
  };

  return (
    <div data-testid="certifications-admin-panel" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={S.card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Icon name="ribbon-outline" style={{ fontSize: 28, color: C.accent }} />
          <div style={{ flex: 1, minWidth: 220 }}>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: C.text, margin: 0, letterSpacing: '-0.01em' }}>
              Certifications
            </h2>
            <p style={{ margin: '4px 0 0', color: C.textMid, fontSize: 13 }}>
              Every certificate HealthCues has issued. Filter by audience, course, status, or date — and revoke / reinstate as needed.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <SummaryPill label="Total" value={filteredCount} color={C.text} />
            <SummaryPill label="Valid" value={validCount} color={C.green} />
            <SummaryPill label="Revoked" value={revokedCount} color={C.red} />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={S.card}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
          <FilterField label="Search">
            <input
              type="text"
              data-testid="cert-filter-search"
              placeholder="Name, email, course, cert id…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ ...inputStyle, minWidth: 240 }}
            />
          </FilterField>
          <FilterField label="Audience">
            <select
              data-testid="cert-filter-audience"
              value={audienceFilter}
              onChange={(e) => setAudienceFilter(e.target.value)}
              style={inputStyle}
            >
              <option value="">All audiences</option>
              {AUDIENCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </FilterField>
          <FilterField label="Course">
            <select
              data-testid="cert-filter-course"
              value={courseFilter}
              onChange={(e) => setCourseFilter(e.target.value)}
              style={{ ...inputStyle, minWidth: 220 }}
            >
              <option value="">All courses</option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
          </FilterField>
          <FilterField label="Status">
            <select
              data-testid="cert-filter-status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={inputStyle}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </FilterField>
          <FilterField label="Issued from">
            <input
              type="date"
              data-testid="cert-filter-date-from"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={inputStyle}
            />
          </FilterField>
          <FilterField label="Issued to">
            <input
              type="date"
              data-testid="cert-filter-date-to"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={inputStyle}
            />
          </FilterField>
          <button
            type="button"
            data-testid="cert-filter-clear"
            onClick={clearFilters}
            style={{ ...S.buttonSecondary, fontSize: 12 }}
          >
            Clear filters
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table data-testid="cert-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                <Th>Issued</Th>
                <Th>Learner</Th>
                <Th>Audience</Th>
                <Th>Course</Th>
                <Th align="right">Score</Th>
                <Th align="right">Version</Th>
                <Th>Status</Th>
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: C.textLight, fontStyle: 'italic' }}>Loading certificates…</td></tr>
              )}
              {!loading && certs.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: C.textLight }}>
                  No certificates match your filters.
                </td></tr>
              )}
              {!loading && certs.map((cert) => {
                const status = statusBadge(cert);
                const aud = audienceBadge(cert.audience);
                return (
                  <tr key={cert.id} data-testid={`cert-row-${cert.id}`} style={{ borderBottom: `1px solid ${C.borderLight}` }}>
                    <Td>
                      <div style={{ fontWeight: 600, color: C.text }}>{formatDate(cert.issuedAt)}</div>
                      <div style={{ fontSize: 11, color: C.textLight, fontFamily: "'JetBrains Mono', monospace" }}>{cert.id}</div>
                    </Td>
                    <Td>
                      <div style={{ fontWeight: 600, color: C.text }}>{cert.userName || '—'}</div>
                      <div style={{ fontSize: 11, color: C.textMid }}>{cert.userEmail || ''}</div>
                    </Td>
                    <Td>
                      <Pill bg={aud.bg} fg={aud.fg}>{cert.audience ? audienceLabel(cert.audience) : '—'}</Pill>
                    </Td>
                    <Td>
                      <div style={{ fontWeight: 500, color: C.text }}>{cert.courseTitle || '—'}</div>
                    </Td>
                    <Td align="right">
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: C.text }}>
                        {cert.scorePercent ?? '—'}%
                      </span>
                      {cert.passPercent ? (
                        <span style={{ fontSize: 10, color: C.textLight, marginLeft: 4 }}>/ {cert.passPercent}%</span>
                      ) : null}
                    </Td>
                    <Td align="right">
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", color: C.textMid }}>v{cert.courseVersion ?? 1}</span>
                    </Td>
                    <Td>
                      <Pill bg={status.bg} fg={status.fg} border={status.border}>{status.label}</Pill>
                    </Td>
                    <Td align="right">
                      <div style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <IconButton
                          testId={`cert-open-${cert.id}`}
                          title="Open verification page"
                          icon="open-outline"
                          onClick={() => handleOpenVerify(cert)}
                        />
                        <IconButton
                          testId={`cert-copy-${cert.id}`}
                          title="Copy verification link"
                          icon="copy-outline"
                          onClick={() => handleCopyLink(cert)}
                        />
                        {cert.revokedAt ? (
                          <IconButton
                            testId={`cert-reinstate-${cert.id}`}
                            title="Reinstate certificate"
                            icon="refresh-outline"
                            color={C.green}
                            onClick={() => handleReinstate(cert)}
                          />
                        ) : (
                          <IconButton
                            testId={`cert-revoke-${cert.id}`}
                            title="Revoke certificate"
                            icon="ban-outline"
                            color={C.red}
                            onClick={() => handleRevoke(cert)}
                          />
                        )}
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Local subcomponents
// ----------------------------------------------------------------------------

const inputStyle = {
  padding: '8px 12px',
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  background: '#fff',
  fontSize: 13,
  fontFamily: 'inherit',
  color: C.text,
  outline: 'none',
};

function FilterField({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: C.textMid, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function SummaryPill({ label, value, color }) {
  return (
    <div style={{
      padding: '8px 14px',
      borderRadius: 999,
      background: '#fff',
      border: `1px solid ${C.border}`,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      fontSize: 12,
    }}>
      <span style={{ color: C.textMid, fontWeight: 600 }}>{label}</span>
      <span style={{ color, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace" }}>{value}</span>
    </div>
  );
}

function Th({ children, align = 'left' }) {
  return (
    <th style={{
      textAlign: align,
      padding: '10px 14px',
      fontSize: 11,
      fontWeight: 700,
      color: C.textMid,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
    }}>{children}</th>
  );
}

function Td({ children, align = 'left' }) {
  return (
    <td style={{
      padding: '12px 14px',
      verticalAlign: 'top',
      textAlign: align,
      color: C.text,
    }}>{children}</td>
  );
}

function Pill({ children, bg, fg, border }) {
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '3px 10px',
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      background: bg,
      color: fg,
      border: border || `1px solid ${bg}`,
    }}>{children}</span>
  );
}

function IconButton({ icon, onClick, title, color, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      title={title}
      onClick={onClick}
      style={{
        background: '#fff',
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        padding: '6px 8px',
        cursor: 'pointer',
        color: color || C.textMid,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background 0.15s, border-color 0.15s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = C.bg; e.currentTarget.style.borderColor = color || C.textMid; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = C.border; }}
    >
      <Icon name={icon} style={{ fontSize: 16 }} />
    </button>
  );
}
